import { ChangeEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DonationModel from '../../models/DonationModel';
import Loading from '../shared/Loading';

const DonationWinners = () => {
    //
    // State
    //
    const [donations, setDonations] = useState<DonationModel[]>([]);
    const [visibleDonations, setVisibleDonations] = useState<DonationModel[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch donations with winners
    useEffect(() => {
        const fetchDonationWinners = async () => {
            setLoading(true);
            try {
                const resp = await fetch("/api/donation/get-winners", {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (resp.status !== 200) {
                    console.error("Failed to fetch donation winners:", resp);
                    return;
                }

                const data = await resp.json();
                if (data && Array.isArray(data.donations)) {
                    // Sort donations by name alphabetically
                    const sortedDonations = data.donations.sort((a: DonationModel, b: DonationModel) =>
                        (a.name || '').localeCompare(b.name || '')
                    );

                    setDonations(sortedDonations);
                    setVisibleDonations(sortedDonations);
                } else {
                    console.error("Invalid response format:", data);
                    setDonations([]);
                    setVisibleDonations([]);
                }
            } catch (error) {
                console.error("Error fetching donation winners:", error);
                setDonations([]);
                setVisibleDonations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDonationWinners();
    }, []);

    // Handle search
    const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
        const search = e.target.value.toLowerCase();

        if (!search.trim()) {
            setVisibleDonations(donations);
            return;
        }

        const filteredDonations = donations.filter((donation) => {
            return (
                (donation.name?.toLowerCase().includes(search)) ||
                (donation.winnerName?.toLowerCase().includes(search))
            );
        });

        setVisibleDonations(filteredDonations);
    };

    // Format phone for display in the table
    const formatPhoneForDisplay = (phoneNumber: string | undefined | null): string => {
        // Handle null/undefined
        if (phoneNumber === null || phoneNumber === undefined) return '';

        // Strip all non-numeric characters
        const cleaned = phoneNumber.replace(/\D/g, '');

        // Format as (XXX) XXX-XXXX if we have enough digits
        if (cleaned.length >= 10) {
            return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6, 10)}`;
        }

        // Otherwise return the cleaned number
        return cleaned;
    };

    if (loading) {
        return (
            <div className="m-2">
                <Loading />
            </div>
        );
    }

    // If no donations found, return a message
    if (donations.length === 0) {
        return (
            <div className="container pt-4">
                <div className="py-lg-3 py-md-2 py-sm-2 px-2">
                    <div className="row">
                        <div className="col-8">
                            <h2 className="text-dark">Basket Winners</h2>
                        </div>
                        <div className="col-4 text-end pb-2">
                            <Link to="/" className="btn btn-primary">
                                <i className="bi bi-house"></i> Home
                            </Link>
                        </div>
                        <hr />
                    </div>
                    <h2>No Basket Winners Found</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="container pt-4">
            <div className="py-lg-3 py-md-2 py-sm-2 px-2">
                <div className="row">
                    <div className="col-8">
                        <h2 className="text-dark">Basket Winners</h2>
                    </div>
                    <div className="col-4 text-end pb-2">
                        <Link to="/" className="btn btn-primary">
                            <i className="bi bi-house"></i> Home
                        </Link>
                    </div>
                    <hr />
                </div>

                <div className="my-3 row ml-3 mr-3">
                    <div className="col-12">
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <div className="input-group-text search-input-icon py-2">
                                    <i className="bi bi-search"></i>
                                </div>
                            </div>
                            <input
                                className="form-control py-2 border-right-0 border"
                                type="search"
                                placeholder="Search"
                                autoFocus
                                onChange={(e) => handleSearch(e)}
                            />
                        </div>
                    </div>
                </div>

                {visibleDonations.length === 0 ? (
                    <div className="alert alert-info" role="alert">
                        No matching baskets found.
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead className="thead-light">
                                <tr>
                                    <th scope="col">Basket</th>
                                    <th scope="col">Value</th>
                                    <th scope="col">Winner</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleDonations.map((donation, index) => (
                                    <tr key={index}>
                                        <td className="font-weight-bold">{donation.name}</td>
                                        <td>${donation.value}</td>
                                        <td>{donation.winnerName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DonationWinners;